import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, User, Coins, Check, Zap } from 'lucide-react';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedTier, setSelectedTier] = useState<'bronze' | 'silver' | 'gold'>('bronze');
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle registration logic here
    console.log('Registration attempt:', { ...formData, selectedTier, agreeToTerms });
  };

  const tiers = [
    {
      id: 'bronze',
      name: 'Bronze',
      price: 'Free',
      features: [
        '10 trades per month',
        '5 AI-assisted trades',
        'Basic competitions',
        'Community access'
      ],
      color: 'from-orange-400 to-orange-600',
      popular: false
    },
    {
      id: 'silver',
      name: 'Silver',
      price: '$29/month',
      features: [
        '50 trades per month',
        '20 AI-assisted trades',
        'Premium competitions',
        'Advanced analytics',
        'Email support'
      ],
      color: 'from-gray-400 to-gray-600',
      popular: true
    },
    {
      id: 'gold',
      name: 'Gold',
      price: '$79/month',
      features: [
        'Unlimited trades',
        '50 AI-assisted trades',
        'Elite competitions',
        'Real-time alerts',
        'Priority support',
        'Custom strategies'
      ],
      color: 'from-win-dark-gold to-win-dark-gold-dark',
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-win-dark-bg">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-win-dark-gold to-win-dark-gold-dark rounded-3xl flex items-center justify-center">
              <Coins className="w-6 h-6 text-win-dark-bg" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-win-dark-text">X Gold</h1>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-win-dark-text mb-2">Create Your Account</h2>
          <p className="text-win-dark-text-muted">Join thousands of traders and start your gold trading journey</p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Registration Form */}
            <div className="win-card p-8">
              <h3 className="text-xl font-bold text-win-dark-text mb-6">Account Information</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-win-dark-text mb-2">
                      First Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-win-dark-text-muted" />
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder="John"
                        className="win-input w-full pl-12"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-win-dark-text mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Doe"
                      className="win-input w-full"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-win-dark-text mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-win-dark-text-muted" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="john@example.com"
                      className="win-input w-full pl-12"
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-win-dark-text mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-win-dark-text-muted" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create a strong password"
                      className="win-input w-full pl-12 pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-win-dark-text-muted hover:text-win-dark-text"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-win-dark-text mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-win-dark-text-muted" />
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm your password"
                      className="win-input w-full pl-12 pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-win-dark-text-muted hover:text-win-dark-text"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Terms Agreement */}
                <div className="flex items-start gap-3">
                  <input
                    id="terms"
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="w-5 h-5 mt-0.5 rounded border-win-dark-border bg-win-dark-surface focus:ring-win-dark-accent focus:ring-2"
                    required
                  />
                  <label htmlFor="terms" className="text-sm text-win-dark-text-muted">
                    I agree to the{' '}
                    <Link to="/terms" className="text-win-dark-accent hover:text-win-dark-accent-hover">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link to="/privacy" className="text-win-dark-accent hover:text-win-dark-accent-hover">
                      Privacy Policy
                    </Link>
                  </label>
                </div>

                {/* Register Button */}
                <button
                  type="submit"
                  disabled={!agreeToTerms}
                  className="w-full win-button py-4 text-lg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Account
                </button>
              </form>

              {/* Login Link */}
              <div className="text-center mt-6">
                <p className="text-win-dark-text-muted">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-win-dark-accent hover:text-win-dark-accent-hover font-medium"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </div>

            {/* Subscription Plans */}
            <div className="win-card p-8">
              <h3 className="text-xl font-bold text-win-dark-text mb-6">Choose Your Plan</h3>
              
              <div className="space-y-4">
                {tiers.map((tier) => (
                  <div
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id as any)}
                    className={`relative p-6 rounded-3xl border cursor-pointer transition-all duration-200 ${
                      selectedTier === tier.id
                        ? 'border-win-dark-accent bg-win-dark-accent/10'
                        : 'border-win-dark-border hover:border-win-dark-accent/50'
                    }`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <span className="bg-win-dark-accent text-white px-4 py-1 rounded-full text-sm font-medium">
                          Most Popular
                        </span>
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-8 h-8 bg-gradient-to-r ${tier.color} rounded-2xl`}></div>
                          <h4 className="text-lg font-bold text-win-dark-text">{tier.name}</h4>
                        </div>
                        <p className="text-2xl font-bold text-win-dark-text">{tier.price}</p>
                      </div>
                      
                      <div className={`w-6 h-6 rounded-full border-2 ${
                        selectedTier === tier.id
                          ? 'border-win-dark-accent bg-win-dark-accent'
                          : 'border-win-dark-border'
                      }`}>
                        {selectedTier === tier.id && (
                          <Check className="w-3 h-3 text-white m-0.5" />
                        )}
                      </div>
                    </div>
                    
                    <ul className="space-y-2">
                      {tier.features.map((feature, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-win-dark-text-muted">
                          <Check className="w-4 h-4 text-green-400" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Trial Notice */}
              <div className="mt-6 p-4 bg-win-dark-surface-light rounded-3xl">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-win-dark-gold mt-0.5" />
                  <div>
                    <p className="font-medium text-win-dark-text mb-1">7-Day Free Trial</p>
                    <p className="text-sm text-win-dark-text-muted">
                      Start with any paid plan and get 7 days free. Cancel anytime during the trial period.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;